import React, { useState } from "react";
import { OrganizationUserDto } from "../common/ObjectTypes";
import UserActionsModal from "../common/UserActionsModal";
import { BeatLoader } from "react-spinners";
import {
  getOrganizationUsers,
  reinviteOrganizationUser,
  removeUserFromOrganization,
  updateOrgUserRole,
} from "../../api/organizationsApi";
import { toast } from "react-toastify";
import { ActionTypes, OrganizationRoles } from "../common/Enums";
import UsersSearchBar from "../common/UserSearchBar";
import { useTranslation } from "react-i18next";
import ActiveUsersTable from "../common/ActiveUsersTable";
import PendingUsersTable from "../common/PendingUsersTable";
import { getSendOrRemoveInvitePermissionsFromOrgRole } from "../common/helpers";
import { useProfile } from "../common/ProfileProvider";
import { useNavigate } from "react-router-dom";
import tailwindClassnames from "../common/TailwindClassnames";

type OrganizationUsersTabProps = {
  selectedUser: OrganizationUserDto | undefined; // Allow undefined
  isOpen: boolean;
  onClose: () => void;
  activeUsers: OrganizationUserDto[];
  pendingUsers: OrganizationUserDto[];
  userSearchQuery: any;
  take: number;
  totalCount: number;
  offset: number;
  onPageClick: Function;
  onInviteUser: Function;
  onSelectUser: Function;
  loading: boolean;
  sendingInvite: boolean;
  roles: any[];
  orgId: number;
  orgName: string;
  perms: any[];
  loadUsersAndRoles: Function;
  onResendInvitation: Function;
  onRemoveInvitation: Function;
  onChangeSearch: Function;
  onSearch: Function;
  onChangeSelectRole: Function;
  handleRoleSelectEnter: Function;
};

const OrganizationUsersTab: React.FC<OrganizationUsersTabProps> = ({
  selectedUser,
  isOpen,
  onClose,
  activeUsers,
  pendingUsers,
  userSearchQuery,
  take,
  totalCount,
  offset,
  onPageClick,
  onInviteUser,
  onSelectUser,
  loading,
  roles,
  orgId,
  orgName,
  perms,
  loadUsersAndRoles,
  onResendInvitation,
  onRemoveInvitation,
  onChangeSearch,
  onSearch,
  onChangeSelectRole,
  sendingInvite,
  handleRoleSelectEnter,
}) => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(false);
  const { profile, fetchProfile } = useProfile();
  const navigate = useNavigate();

  const handleActionSend = async (action_id: string, role_id: string) => {
    if (selectedUser === undefined) {
      return;
    }
    setIsSending(true);
    try {
      switch (action_id) {
        case ActionTypes.CHANGE_ROLE:
          try {
            const temp = await getOrganizationUsers({
              org_id: Number(orgId),
              all_users: true,
            });
            const activeUsersForOrg = temp.users.filter(
              (user: any) => user.is_verified,
            );
            if (
              activeUsersForOrg.filter(
                (user: OrganizationUserDto) =>
                  user.role_id === OrganizationRoles.OWNER,
              ).length === 1 &&
              selectedUser.role_id === OrganizationRoles.OWNER
            ) {
              toast.error(
                `${"User"} ${t("is the only owner of that organization.")} ${t(
                  "Please assign another owner before removing this user.",
                )}`,
              );
            } else {
              await updateOrgUserRole(orgId, selectedUser.user_id, {
                role_id: role_id,
              });
              toast.success(t("Role Updated"));
              // reload user perms if needed
              if (selectedUser.user_id === profile?.user_id) {
                await fetchProfile();
              }
            }
          } catch (err) {
            console.error(err);
            toast.error(t("Error Updating Role"));
          }
          break;
        case ActionTypes.RESEND_INVITATION:
          try {
            await reinviteOrganizationUser(orgId, selectedUser.user_id, orgId);
            toast.success(t("Invitation Resent"));
          } catch (err) {
            toast.error(t("Error Resending Invitation"));
          }
          break;
        case ActionTypes.REMOVE_USER:
          if (
            activeUsers.filter((user) => user.role_name === "OWNER").length ===
              1 &&
            selectedUser.role_name === "OWNER"
          ) {
            toast.error(
              `${selectedUser.first_name} ${selectedUser.last_name} ${t(
                "is the only owner of that organization.",
              )} ${t(
                "Please assign another owner before removing this user.",
              )}`,
            );
          } else {
            try {
              await removeUserFromOrganization(orgId, selectedUser.user_id);
              toast.success(t("User Removed from Organization"));
            } catch (err) {
              toast.error(t("Error Removing User from Organization"));
            }
            if (selectedUser.user_id === profile?.user_id) {
              onClose();
              setIsSending(false);
              // redirect this user if they are removed from the organization
              navigate("/organizations");
              await fetchProfile(); // reload user perms if needed
              return; // return early to avoid calling loadUsersAndRoles()
            }
          }
          break;
        default:
          console.log("Unknown action");
      }
      await loadUsersAndRoles();
      onClose();
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex flex-row w-full">
      <UserActionsModal
        isOpen={isOpen}
        onClose={() => onClose()}
        selectedUser={selectedUser}
        roles={roles}
        scope="organization"
        onSend={handleActionSend}
        perms={perms}
        entityName={orgName}
        isSending={isSending}
      />
      <div className="overflow-x-auto w-full mx-8 mt-8">
        <UsersSearchBar
          userSearchQuery={userSearchQuery}
          onInviteUser={onInviteUser}
          loading={loading}
          roles={roles}
          onChangeSearch={onChangeSearch}
          onSearch={onSearch}
          onChangeSelectRole={onChangeSelectRole}
          handleRoleSelectEnter={handleRoleSelectEnter}
          sendingInvite={sendingInvite}
          hasInvitePermission={perms.some((perm: any) =>
            perm.name.includes("INVITE_USER"),
          )}
        />
        {loading ? (
          <div className="sweet-loading flex justify-center w-full mt-8">
            <BeatLoader
              color={tailwindClassnames.skyBlueDark}
              loading={true}
              size={15}
              margin={5}
            />
          </div>
        ) : (
          <>
            <ActiveUsersTable
              activeUsers={activeUsers}
              onSelectUser={onSelectUser}
              take={take}
              totalCount={totalCount}
              offset={offset}
              resultsLoaded={!loading}
              onPageClick={onPageClick}
            />
            <PendingUsersTable
              pendingUsers={pendingUsers}
              onResendInvitation={onResendInvitation}
              onRemoveInvitation={onRemoveInvitation}
              getRemoveOrResendInvitePermissionFromUserRole={(
                role: OrganizationRoles,
              ) => getSendOrRemoveInvitePermissionsFromOrgRole(role, perms)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OrganizationUsersTab;
